import React, { useState } from "react";
import { Mail, Loader2, Check } from "lucide-react";
import { Button } from "./ui/button";
import { api } from "../lib/api";

/**
 * DigestSignup — slim hero-adjacent email capture for the free Sunday-night
 * macro pulse. No vetted tickers (those are the paid product) — the pulse
 * is deliberately top-of-funnel: taste the analyst voice, then convert.
 *
 * Copy is chosen to make it clear this is macro-only + free, not the alerts.
 */
export default function DigestSignup() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (state === "loading" || state === "done") return;
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@")) {
      setState("error");
      setMessage("Enter a valid email");
      return;
    }
    setState("loading");
    setMessage("");
    try {
      const r = await api.post("/digest/subscribe", { email: trimmed });
      setState("done");
      setMessage(
        r.data?.already_subscribed
          ? "You're already on the list — Sunday 8pm ET."
          : "You're in. First pulse lands Sunday 8pm ET."
      );
    } catch (err) {
      setState("error");
      setMessage(err?.response?.data?.detail || "Something went wrong, try again");
    }
  };

  return (
    <div className="mt-8 flex flex-col items-center text-center" data-testid="digest-signup">
      <div className="flex items-center gap-2 text-sm text-neutral-400 mb-2">
        <Mail className="w-3.5 h-3.5" />
        <span>Free · Sunday-night market pulse · No tickers</span>
      </div>
      <form
        onSubmit={submit}
        className="flex flex-col sm:flex-row gap-2 w-full max-w-md"
        aria-label="Subscribe to the free weekly macro pulse"
      >
        <input
          type="email"
          value={email}
          onChange={(e) => { setEmail(e.target.value); if (state === "error") setState("idle"); }}
          placeholder="you@inbox.com"
          disabled={state === "loading" || state === "done"}
          style={{ backgroundColor: "#150932", color: "#EEE9F5" }}
          className="flex-1 rounded-md border border-mb-emerald/30 px-4 h-11 text-sm placeholder:text-neutral-500 focus:outline-none focus:ring-2 focus:ring-mb-emerald/50 focus:border-mb-emerald disabled:opacity-60"
          data-testid="digest-signup-email"
          autoComplete="email"
        />
        <Button
          type="submit"
          size="lg"
          disabled={state === "loading" || state === "done"}
          style={
            state === "done"
              ? { backgroundColor: "#A855F7", color: "#FFFFFF", borderWidth: 0 }
              : { backgroundColor: "#FFFFFF", color: "#0A0518", borderWidth: 0 }
          }
          className="rounded-md h-11 px-5 font-bold whitespace-nowrap transition-colors hover:brightness-95 disabled:opacity-100"
          data-testid="digest-signup-submit"
        >
          {state === "loading" && <Loader2 className="w-4 h-4 animate-spin" />}
          {state === "done" && <Check className="w-4 h-4" />}
          {state !== "loading" && state !== "done" && "Get the free pulse"}
        </Button>
      </form>
      {message && (
        <div
          className={`mt-2 text-xs ${state === "error" ? "text-red-600" : "text-mb-emerald"}`}
          data-testid="digest-signup-message"
          role={state === "error" ? "alert" : "status"}
        >
          {message}
        </div>
      )}
      <p className="mt-2 text-[11px] text-neutral-500 max-w-md leading-relaxed">
        Regime, fear/greed, and one paragraph on what the desk is watching. Unsubscribe with one click. Want the vetted alerts? <span className="text-mb-emerald font-semibold">That's the paid tier.</span>
      </p>
    </div>
  );
}
